import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useModalize } from 'react-native-modalize'

import { EstimationStatus } from '@ambire-common/controllers/estimation/types'
import { SignAccountOpType } from '@ambire-common/controllers/signAccountOp/helper'
import {
  SignAccountOpUpdateProps,
  SigningStatus
} from '@ambire-common/controllers/signAccountOp/signAccountOp'
import { Key } from '@ambire-common/interfaces/keystore'
import { ISignAccountOpController } from '@ambire-common/interfaces/signAccountOp'
import { i18nKey } from '@common/config/localization/constants'
import usePrevious from '@common/hooks/usePrevious'
import useBackgroundService from '@web/hooks/useBackgroundService'
import useNetworksControllerState from '@web/hooks/useNetworksControllerState'
import useLedger from '@web/modules/hardware-wallet/hooks/useLedger'
import useQrSigningFlow from '@web/modules/hardware-wallet/hooks/useQrSigningFlow'
import { OneClickEstimationProps } from '@web/modules/sign-account-op/components/OneClick/Estimation/Estimation'
import { getIsSignLoading } from '@web/modules/sign-account-op/utils/helpers'

/**
 * How long a second sign+broadcast dispatch is ignored after the first one.
 * Covers both a same-tick synthetic double click and a human double click,
 * while staying far below the time it takes a user to react to a failed
 * broadcast and press Sign again.
 */
const REPEAT_BROADCAST_GUARD_MS = 1500

const PRIMARY_BUTTON_LABELS: Record<
  OneClickEstimationProps['updateType'] | 'Bridge' | 'Sign' | 'HW',
  { default: string; isLoading: string }
> = {
  'Swap&Bridge': {
    default: i18nKey('Swap'),
    isLoading: i18nKey('Swapping...')
  },
  // Same sheet as Swap, but a cross-chain route must not say "Swap".
  Bridge: {
    default: i18nKey('Bridge'),
    isLoading: i18nKey('Bridging...')
  },
  'Transfer&TopUp': {
    default: i18nKey('Send'),
    isLoading: i18nKey('Sending...')
  },
  Sign: {
    default: i18nKey('Sign'),
    isLoading: i18nKey('Signing...')
  },
  HW: {
    default: i18nKey('Begin signing'),
    isLoading: i18nKey('Signing...')
  }
}

type Props = {
  handleUpdateStatus: (status: SigningStatus) => void
  handleUpdate: (params: SignAccountOpUpdateProps) => void
  signAccountOpState: ISignAccountOpController | null
  isOneClickSign?: boolean
  updateType?: OneClickEstimationProps['updateType'] | undefined
  /** Swap&Bridge only: the selected route changes chain. */
  isBridge?: boolean
}

const useSign = ({
  handleUpdateStatus,
  signAccountOpState,
  handleUpdate,
  isOneClickSign,
  updateType = undefined,
  isBridge = false
}: Props) => {
  const { t } = useTranslation()
  const { networks } = useNetworksControllerState()
  const { dispatch } = useBackgroundService()
  const [isChooseSignerShown, setIsChooseSignerShown] = useState(false)
  const [isChooseFeePayerKeyShown, setIsChooseFeePayerKeyShown] = useState(false)
  const [shouldDisplayLedgerConnectModal, setShouldDisplayLedgerConnectModal] = useState(false)
  const [shouldDisplayQrSigningModal, setShouldDisplayQrSigningModal] = useState(false)
  const prevIsChooseSignerShown = usePrevious(isChooseSignerShown)
  const { isLedgerConnected } = useLedger()
  const {
    currentRequest,
    signingStep,
    moveToResponseScan,
    moveBack,
    submitSignatureResponse,
    signingCleanup
  } = useQrSigningFlow()
  const [slowRequest, setSlowRequest] = useState<boolean>(false)
  const [slowPaymasterRequest, setSlowPaymasterRequest] = useState<boolean>(true)
  const [acknowledgedWarnings, setAcknowledgedWarnings] = useState<string[]>([])
  const { ref: warningModalRef, open: openWarningModal, close: closeWarningModal } = useModalize()

  const hasEstimation = useMemo(
    () =>
      signAccountOpState?.isInitialized &&
      !!signAccountOpState?.gasPrices &&
      !signAccountOpState.estimation.error,
    [
      signAccountOpState?.estimation?.error,
      signAccountOpState?.gasPrices,
      signAccountOpState?.isInitialized
    ]
  )

  useEffect(() => {
    // Ensures user can re-open the modal, if previously being closed, e.g.
    // there is an error (modal closed), but user opts-in sign again (open it).
    const isModalStillOpen = isChooseSignerShown && prevIsChooseSignerShown
    // These errors get displayed in the UI (in the <Warning /> component),
    // so in case of an error, closing the signer key selection modal is needed,
    // otherwise errors will be displayed behind the modal overlay.
    if (isModalStillOpen && !!signAccountOpState?.errors.length) {
      setIsChooseSignerShown(false)
    }
  }, [isChooseSignerShown, prevIsChooseSignerShown, signAccountOpState?.errors.length])

  /** See REPEAT_BROADCAST_GUARD_MS and handleBroadcast. */
  const lastBroadcastDispatchAtRef = useRef(0)

  // Also count "queued behind a previous broadcast" as a loading state so the
  // Sign button shows progress while the wallet waits for the prior tx on the
  // same (account, chain) to finish broadcasting before handing off.
  const isSignLoading =
    getIsSignLoading(signAccountOpState?.status) ||
    !!signAccountOpState?.isSignQueuedAfterPreviousBroadcast

  useEffect(() => {
    if (signAccountOpState?.estimation.estimationRetryError) {
      setSlowRequest(false)
      return
    }
    const timeout = setTimeout(() => {
      // set the request to slow if the state is not init (no estimation)
      // or the gas prices haven't been fetched
      if (!signAccountOpState?.isInitialized || !signAccountOpState?.gasPrices) {
        setSlowRequest(true)
      }
    }, 5000)

    if (signAccountOpState?.isInitialized && !!signAccountOpState?.gasPrices) {
      clearTimeout(timeout)
      setSlowRequest(false)
    }

    return () => {
      clearTimeout(timeout)
    }
  }, [
    signAccountOpState?.isInitialized,
    signAccountOpState?.gasPrices,
    signAccountOpState?.estimation.estimationRetryError
  ])

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (slowPaymasterRequest) return

      if (signAccountOpState?.status?.type === SigningStatus.WaitingForPaymaster) {
        setSlowPaymasterRequest(true)
        openWarningModal()
      }
    }, 3000)

    if (signAccountOpState?.status?.type !== SigningStatus.WaitingForPaymaster) {
      clearTimeout(timeout)
      setSlowPaymasterRequest(false)
      if (slowPaymasterRequest) closeWarningModal()
    }

    return () => {
      clearTimeout(timeout)
    }
  }, [closeWarningModal, openWarningModal, signAccountOpState?.status?.type, slowPaymasterRequest])

  const network = useMemo(() => {
    return networks.find((n) => n.chainId === signAccountOpState?.accountOp?.chainId)
  }, [networks, signAccountOpState?.accountOp?.chainId])

  const signingKeyType = signAccountOpState?.accountOp?.signingKeyType
  const feePayerKeyType = signAccountOpState?.accountOp?.gasFeePayment?.paidByKeyType
  const isAtLeastOneOfTheKeysInvolvedLedger =
    signingKeyType === 'ledger' || feePayerKeyType === 'ledger'

  const handleDismissLedgerConnectModal = useCallback(() => {
    setShouldDisplayLedgerConnectModal(false)
  }, [])

  const handleQrSigningFlowOnClosePressed = useCallback(
    () => setShouldDisplayQrSigningModal(false),
    []
  )

  const handleQrSingingFlowOnContinuePressed = moveToResponseScan
  const handleQrSigningFlowOnBackPressed = moveBack
  const handleQrSigningFlowSubmitSignatureResponse = submitSignatureResponse

  const handleQrSigningFlowOnRejectPressed = useCallback(() => {
    signingCleanup()
    setShouldDisplayQrSigningModal(false)
  }, [signingCleanup])

  const warningToPromptBeforeSign = useMemo(
    () =>
      signAccountOpState?.warnings.find((warning) => {
        const signingType = isOneClickSign ? 'one-click-sign' : 'sign'
        const shouldPrompt = warning.promptBefore?.includes(signingType)

        if (!shouldPrompt) return false

        const isWarningAcknowledged = acknowledgedWarnings.includes(warning.id)

        return !isWarningAcknowledged
      }),
    [acknowledgedWarnings, isOneClickSign, signAccountOpState?.warnings]
  )

  const handleBroadcast = useCallback(() => {
    if (!signAccountOpState) return // should never happen

    // Swallow a repeat press that lands before the controller state has made it
    // back to the UI. `isSignDisabled` is derived from `isSignLoading`, which
    // only flips once the background/worker round-trip completes — so a real
    // double-click (or two synthetic clicks bubbling from a nested node) both
    // read the button as enabled and both dispatch. The second dispatch then
    // hits the re-entrancy guard in SignAccountOpController.signAndBroadcast
    // and surfaces a `major` red toast ("...already in progress") on top of a
    // transaction that is broadcasting perfectly fine.
    //
    // A timestamp, not a latch: it self-heals, so a genuine retry after a
    // failed broadcast is never blocked (the user cannot see the failure and
    // press again inside this window).
    if (Date.now() - lastBroadcastDispatchAtRef.current < REPEAT_BROADCAST_GUARD_MS) return
    lastBroadcastDispatchAtRef.current = Date.now()

    let type: SignAccountOpType = 'default'

    if (updateType === 'Swap&Bridge') {
      type = 'one-click-swap-and-bridge'
    }

    if (updateType === 'Transfer&TopUp') {
      type = 'one-click-transfer'
    }
    dispatch({
      type: 'MAIN_CONTROLLER_HANDLE_SIGN_AND_BROADCAST_ACCOUNT_OP',
      params: { type, fromRequestId: signAccountOpState.fromRequestId }
    })
  }, [dispatch, signAccountOpState, updateType])

  const handleSign = useCallback(
    (_chosenSigningKeyType?: Key['type'], _warningAccepted?: boolean) => {
      // Prioritize warning(s) modals over all others
      // Warning modals are not displayed in the one-click swap flow
      if (warningToPromptBeforeSign && !_warningAccepted) {
        openWarningModal()
        handleUpdateStatus(SigningStatus.UpdatesPaused)
        return
      }

      const isExternalQr =
        _chosenSigningKeyType === 'qr' ||
        signAccountOpState?.accountOp.signingKeyType === 'qr' ||
        signAccountOpState?.accountOp.gasFeePayment?.paidByKeyType === 'qr'
      const isFeePayerSameAsSigner =
        signAccountOpState?.accountOp.signingKeyAddr ===
        signAccountOpState?.accountOp.gasFeePayment?.paidBy
      const isLedgerKeyInvolvedInTheJustChosenKeys = _chosenSigningKeyType
        ? _chosenSigningKeyType === 'ledger' || feePayerKeyType === 'ledger'
        : isAtLeastOneOfTheKeysInvolvedLedger

      if (isLedgerKeyInvolvedInTheJustChosenKeys && !isLedgerConnected) {
        setShouldDisplayLedgerConnectModal(true)
        return
      }

      if ((signAccountOpState?.feePayerKeyStoreKeys?.length || 0) > 1 && !isFeePayerSameAsSigner) {
        setIsChooseFeePayerKeyShown(true)
        return
      }

      if (isExternalQr) {
        // don't return here — QR flow is part of the signing itself,
        // unlike Ledger where we need to stop and wait for connection
        setShouldDisplayQrSigningModal(true)
      }

      handleBroadcast()
    },
    [
      warningToPromptBeforeSign,
      signAccountOpState?.accountOp.signingKeyType,
      signAccountOpState?.accountOp.gasFeePayment?.paidByKeyType,
      signAccountOpState?.accountOp.signingKeyAddr,
      signAccountOpState?.accountOp.gasFeePayment?.paidBy,
      signAccountOpState?.feePayerKeyStoreKeys?.length,
      feePayerKeyType,
      isAtLeastOneOfTheKeysInvolvedLedger,
      isLedgerConnected,
      handleBroadcast,
      openWarningModal,
      handleUpdateStatus
    ]
  )

  const handleChangeSigningKey = useCallback(
    (signingKeyAddr: Key['addr'], _chosenSigningKeyType: Key['type']) => {
      handleUpdate({ signingKeyAddr, signingKeyType: _chosenSigningKeyType })

      // Explicitly pass the currently selected signing key type, because
      // the signing key type in the state might not be updated yet,
      // and Sign Account Op controller assigns a default signing upfront
      handleSign(_chosenSigningKeyType)
    },
    [handleSign, handleUpdate]
  )

  const handleChangeFeePayerKeyType = useCallback(
    // Done for compatibility with the select component
    (_: Key['addr'], newFeePayerKeyType: Key['type']) => {
      handleUpdate({ paidByKeyType: newFeePayerKeyType })

      handleBroadcast()
    },
    [handleBroadcast, handleUpdate]
  )

  const onSignButtonClick = useCallback(() => {
    if (!signAccountOpState) return

    // If the account has only one signer, we don't need to show the select signer overlay,
    // and we will sign the transaction with the only one available signer (it is set by default in the controller).
    if (signAccountOpState?.accountKeyStoreKeys.length === 1) {
      handleSign()
      return
    }

    setIsChooseSignerShown(true)
  }, [signAccountOpState, handleSign])

  const acknowledgeWarning = useCallback(() => {
    if (!warningToPromptBeforeSign) return

    setAcknowledgedWarnings((prev) => [...prev, warningToPromptBeforeSign.id])
    closeWarningModal()
    handleSign(undefined, true)
  }, [warningToPromptBeforeSign, closeWarningModal, handleSign])

  useEffect(() => {
    if (shouldDisplayLedgerConnectModal && isLedgerConnected) {
      handleDismissLedgerConnectModal()
    }
  }, [handleDismissLedgerConnectModal, shouldDisplayLedgerConnectModal, isLedgerConnected])

  const dismissWarning = useCallback(() => {
    handleUpdateStatus(SigningStatus.ReadyToSign)

    closeWarningModal()
  }, [handleUpdateStatus, closeWarningModal])

  const isViewOnly = useMemo(
    () => signAccountOpState?.accountKeyStoreKeys.length === 0,
    [signAccountOpState?.accountKeyStoreKeys]
  )

  const isAtLeastOneOfTheKeysInvolvedExternal = useMemo(
    () =>
      (!!signingKeyType && signingKeyType !== 'internal') ||
      (!!feePayerKeyType && feePayerKeyType !== 'internal'),
    [feePayerKeyType, signingKeyType]
  )

  const renderedButNotNecessarilyVisibleModal:
    | 'warnings'
    | 'ledger-connect'
    | 'hw-sign'
    | 'qr-sign'
    | null = useMemo(() => {
    // Prioritize warning(s) modals over all others
    if (
      warningToPromptBeforeSign ||
      // We render the warning modal if the paymaster is loading, but
      // don't display it to the user until the paymaster has been loading for too long.
      // This is required because opening the modal isn't possible if it isn't rendered.
      signAccountOpState?.status?.type === SigningStatus.WaitingForPaymaster
    )
      return 'warnings'

    if (shouldDisplayLedgerConnectModal) return 'ledger-connect'

    const hasActiveQrFlow = !!currentRequest || (!!signingStep && signingStep !== 'idle')

    if (shouldDisplayQrSigningModal || hasActiveQrFlow) return 'qr-sign'

    if (isAtLeastOneOfTheKeysInvolvedExternal) return 'hw-sign'

    return null
  }, [
    currentRequest,
    signingStep,
    shouldDisplayQrSigningModal,
    isAtLeastOneOfTheKeysInvolvedExternal,
    shouldDisplayLedgerConnectModal,
    signAccountOpState?.status?.type,
    warningToPromptBeforeSign
  ])

  // Watchdog for the silent QR hang: we asked for a QR signature (the modal is
  // "rendered but not necessarily visible") yet the QrHardwareController state
  // never reached the UI, so `Modals` has nothing to show and the button sits on
  // "Sending…" forever. That is always a state-transport bug (a controller-state
  // hook resolving to a variant whose event channel doesn't exist on this
  // build), never a user-side one — surface it instead of hanging in silence.
  useEffect(() => {
    if (renderedButNotNecessarilyVisibleModal !== 'qr-sign') return
    if (currentRequest && signingStep && signingStep !== 'idle') return

    const timeoutId = setTimeout(() => {
      // eslint-disable-next-line no-console
      console.error(
        '[useSign] QR signing flow is active but the QrHardwareController state never arrived in the UI ' +
          '(currentRequest/signingStep are empty), so the QR sheet cannot render. ' +
          'Check that useQrHardwareControllerState resolves to the variant matching this build.',
        { currentRequest, signingStep }
      )
    }, 4000)

    return () => clearTimeout(timeoutId)
  }, [renderedButNotNecessarilyVisibleModal, currentRequest, signingStep])

  const primaryButtonText = useMemo(() => {
    const buttonLabelType =
      updateType === 'Swap&Bridge' && isBridge
        ? 'Bridge'
        : updateType || (isAtLeastOneOfTheKeysInvolvedExternal ? 'HW' : 'Sign')

    return t(
      isSignLoading
        ? PRIMARY_BUTTON_LABELS[buttonLabelType].isLoading
        : PRIMARY_BUTTON_LABELS[buttonLabelType].default
    )
  }, [isAtLeastOneOfTheKeysInvolvedExternal, isSignLoading, t, updateType, isBridge])

  // When being done, there is a corner case if the sign succeeds, but the broadcast fails.
  // If so, the "Sign" button should NOT be disabled, so the user can retry broadcasting.
  const notReadyToSignButAlsoNotDone =
    !signAccountOpState?.readyToSign && signAccountOpState?.status?.type !== SigningStatus.Done

  const isSignDisabled = useMemo(() => {
    return (
      isViewOnly ||
      isSignLoading ||
      notReadyToSignButAlsoNotDone ||
      !signAccountOpState?.readyToSign ||
      (signAccountOpState && signAccountOpState.estimation.status === EstimationStatus.Loading)
    )
  }, [isViewOnly, isSignLoading, notReadyToSignButAlsoNotDone, signAccountOpState])

  const bundlerNonceDiscrepancy = useMemo(
    () =>
      signAccountOpState?.warnings.find((warning) => warning.id === 'bundler-nonce-discrepancy') ||
      signAccountOpState?.warnings.find((warning) => warning.id === 'bundler-failure'),
    [signAccountOpState?.warnings]
  )

  useEffect(() => {
    const isExternalQr =
      signAccountOpState?.accountOp.signingKeyType === 'qr' ||
      signAccountOpState?.accountOp.gasFeePayment?.paidByKeyType === 'qr'

    const hasActiveQrFlow = !!currentRequest || (!!signingStep && signingStep !== 'idle')

    if (isExternalQr && hasActiveQrFlow) {
      setShouldDisplayQrSigningModal(true)
    }
  }, [
    currentRequest,
    signingStep,
    signAccountOpState?.accountOp.signingKeyType,
    signAccountOpState?.accountOp.gasFeePayment?.paidByKeyType
  ])

  return {
    renderedButNotNecessarilyVisibleModal,
    isViewOnly,
    dismissWarning,
    acknowledgeWarning,
    onSignButtonClick,
    handleChangeSigningKey,
    warningToPromptBeforeSign,
    handleDismissLedgerConnectModal,
    isChooseSignerShown,
    setIsChooseSignerShown,
    slowPaymasterRequest,
    slowRequest,
    isSignLoading,
    hasEstimation,
    warningModalRef,
    signingKeyType,
    feePayerKeyType,
    handleChangeFeePayerKeyType,
    shouldDisplayLedgerConnectModal,
    network,
    notReadyToSignButAlsoNotDone,
    isSignDisabled,
    primaryButtonText,
    bundlerNonceDiscrepancy,
    isChooseFeePayerKeyShown,
    setIsChooseFeePayerKeyShown,
    shouldHoldToProceed: !!signAccountOpState?.banners?.length,
    shouldDisplayQrSigningModal,
    handleQrSingingFlowOnContinuePressed,
    handleQrSigningFlowSubmitSignatureResponse,
    handleQrSigningFlowOnClosePressed,
    handleQrSigningFlowOnRejectPressed,
    handleQrSigningFlowOnBackPressed,
    currentRequest,
    signingStep
  }
}

export default useSign
